import { type DiagramTeam } from '@ukinfo/shared';
import { cn } from '../ui';
import { TeamSwatch } from './PitchDiagram';

/**
 * Werkzeuge des Skizzeneditors: Ids, Beschriftungen, Gruppen und Symbole. Die Leiste selbst steht in `DiagramToolBar.tsx`
 * (kompakte Hauptknoepfe, Sammelknoepfe mit Auswahlfeld; waagerecht oder senkrecht).
 */

export type ToolId =
  | 'select'
  | 'playerA'
  | 'playerB'
  | 'playerN'
  | 'playerC'
  | 'playerD'
  | 'cone'
  | 'pole'
  | 'ring'
  | 'hurdle'
  | 'ball'
  | 'goal'
  | 'zone'
  | 'arrowPASS'
  | 'arrowRUN'
  | 'arrowDRIBBLE'
  | 'arrowSHOT'
  | 'arrowLOFT'
  | 'arrowSWITCH'
  | 'measure'
  | 'text';

export interface ToolDef {
  id: ToolId;
  /** Kurzer Text im Knopf. */
  short: string;
  /** Name in der Zeile "Werkzeug: ...". */
  name: string;
  /** Beschriftung fuer Screenreader und Tooltip. */
  label: string;
  /** Hinweis unter der Zeichenflaeche, solange das Werkzeug aktiv ist. */
  hint: string;
}

const TAP = 'Tippe aufs Feld, um';
const DRAW = 'Ziehe auf dem Feld vom Anfang zum Ende – oder tippe erst den Anfang, dann das Ende.';
/** Hinweis der Pfeilwerkzeuge: Mehrpunkt-Pfeile und Freihand sind sonst nicht zu finden. */
const ARROW_DRAW = 'Ziehen = gerader Pfeil, eine Kurve ziehen = weicher Pfeil · Tippen setzt Punkte, „Fertig“ beendet.';
/** Pass und Schuss bleiben beim Zeichnen gerade: Knicke setzt man nur ausdruecklich in der Auswahl. */
const ARROW_STRAIGHT = 'Ziehen oder zwei Tipps (Anfang, Ende) zeichnen einen geraden Pfeil. Einen Knick setzt „Knick hinzufügen“ in der Auswahl.';

export const TOOLS: ToolDef[] = [
  { id: 'select', short: 'Auswählen', name: 'Auswählen', label: 'Auswählen und verschieben', hint: 'Element antippen zum Markieren, ziehen zum Verschieben. Auf leerer Fläche einen Rahmen aufziehen.' },
  { id: 'playerA', short: 'Team A', name: 'Spieler Team A', label: 'Spieler Team A setzen', hint: `${TAP} einen Spieler von Team A zu setzen.` },
  { id: 'playerB', short: 'Team B', name: 'Spieler Team B', label: 'Spieler Team B setzen', hint: `${TAP} einen Spieler von Team B zu setzen.` },
  { id: 'playerC', short: 'Team C', name: 'Spieler Team C', label: 'Spieler Team C setzen', hint: `${TAP} einen Spieler von Team C zu setzen.` },
  { id: 'playerD', short: 'Team D', name: 'Spieler Team D', label: 'Spieler Team D setzen', hint: `${TAP} einen Spieler von Team D zu setzen.` },
  { id: 'playerN', short: 'Neutral', name: 'Neutraler Spieler', label: 'Neutralen Spieler setzen', hint: `${TAP} einen neutralen Spieler zu setzen.` },
  { id: 'cone', short: 'Hütchen', name: 'Hütchen', label: 'Hütchen setzen', hint: `${TAP} ein Hütchen zu setzen.` },
  { id: 'pole', short: 'Stange', name: 'Slalomstange', label: 'Slalomstange setzen', hint: `${TAP} eine Slalomstange zu setzen.` },
  { id: 'ring', short: 'Ring', name: 'Koordinationsring', label: 'Koordinationsring setzen', hint: `${TAP} einen Koordinationsring zu setzen.` },
  { id: 'hurdle', short: 'Hürde', name: 'Hürde', label: 'Hürde setzen', hint: `${TAP} eine Hürde zu setzen. Die Drehung steht unter dem Feld in der Auswahl.` },
  { id: 'ball', short: 'Ball', name: 'Ball', label: 'Ball setzen', hint: `${TAP} einen Ball zu setzen.` },
  { id: 'goal', short: 'Tor', name: 'Tor', label: 'Tor oder Minitor setzen', hint: `${TAP} ein Tor zu setzen. Größe und Drehung stehen unter dem Feld in der Auswahl.` },
  { id: 'arrowPASS', short: 'Pass', name: 'Pass', label: 'Pass zeichnen', hint: `${ARROW_STRAIGHT} Ebenfalls dort: „Hinwechseln“ – der Passgeber läuft dem Pass nach.` },
  { id: 'arrowLOFT', short: 'Hoher Ball', name: 'Hoher Ball', label: 'Hohen Ball zeichnen', hint: `${DRAW} Der hohe Ball ist eine Flanke, ein Chip oder Flugball; er lässt sich biegen, hat aber keine Knickpunkte.` },
  { id: 'arrowRUN', short: 'Laufweg', name: 'Laufweg', label: 'Laufweg zeichnen', hint: ARROW_DRAW },
  { id: 'arrowDRIBBLE', short: 'Dribbling', name: 'Dribbling', label: 'Dribbling zeichnen', hint: ARROW_DRAW },
  { id: 'arrowSHOT', short: 'Schuss', name: 'Schuss', label: 'Torschuss zeichnen', hint: ARROW_STRAIGHT },
  { id: 'arrowSWITCH', short: 'Wechsel', name: 'Wechsel', label: 'Positionswechsel zeichnen', hint: `${ARROW_DRAW} Der Positionswechsel zeigt, wohin ein Spieler nach seiner Aktion weiterwechselt.` },
  { id: 'zone', short: 'Zone', name: 'Zone', label: 'Zone zeichnen', hint: `${DRAW} Zone: zwei gegenüberliegende Ecken. Form und Farbe stehen direkt unter dem Feld.` },
  { id: 'text', short: 'Text', name: 'Text', label: 'Text setzen', hint: `${TAP} einen Text zu setzen – das Eingabefeld in der Auswahl öffnet sich sofort. Doppelt auf einen Text tippen bearbeitet ihn.` },
  { id: 'measure', short: 'Maß', name: 'Maßlinie', label: 'Maßlinie zeichnen', hint: `${DRAW} Die Beschriftung (z. B. „15 m“) steht in der Auswahl unter dem Feld.` },
];

export type ToolGroupId = 'select' | 'players' | 'material' | 'paths' | 'areas' | 'insert';

export interface ToolGroup {
  id: ToolGroupId;
  title: string;
  /** Die Werkzeuge der Gruppe; "Einfuegen" hat statt Werkzeugen Aktionen (siehe `DiagramToolBar`). */
  tools: readonly ToolId[];
}

export const TOOL_GROUPS: readonly ToolGroup[] = [
  { id: 'select', title: 'Auswählen', tools: ['select'] },
  { id: 'players', title: 'Spieler', tools: ['playerA', 'playerB', 'playerC', 'playerD', 'playerN'] },
  { id: 'material', title: 'Material', tools: ['cone', 'pole', 'ring', 'hurdle', 'ball', 'goal'] },
  { id: 'paths', title: 'Wege', tools: ['arrowPASS', 'arrowLOFT', 'arrowRUN', 'arrowDRIBBLE', 'arrowSHOT', 'arrowSWITCH'] },
  { id: 'areas', title: 'Flächen & Beschriftung', tools: ['zone', 'text', 'measure'] },
  { id: 'insert', title: 'Einfügen', tools: [] },
];

export const toolDef = (id: ToolId): ToolDef => TOOLS.find((entry) => entry.id === id) ?? TOOLS[0]!;
export const groupOfTool = (id: ToolId): ToolGroupId => TOOL_GROUPS.find((group) => group.tools.includes(id))?.id ?? 'select';

/* ------------------------------------------------------------------ Symbole */

/** Kleine Symbole fuer die Knoepfe; reine Dekoration, der Name steht im Knopf. */
export type IconId =
  | ToolId
  | 'undo'
  | 'redo'
  | 'trash'
  | 'clear'
  | 'multi'
  | 'snap'
  | 'grid'
  | 'steps'
  | 'formation'
  | 'setup'
  | 'rotate'
  | 'help'
  | 'align'
  | 'duplicate'
  | 'row'
  | 'block'
  | 'all'
  | 'play'
  | 'close'
  | 'expand'
  | 'view';

export function ToolIcon({ id, className = 'h-5 w-5' }: { id: IconId; className?: string }): JSX.Element {
  const svg = (children: JSX.Element): JSX.Element => (
    <svg viewBox="0 0 24 24" className={cn(className, 'shrink-0')} aria-hidden="true">
      {children}
    </svg>
  );
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
  switch (id) {
    case 'select':
      return svg(<path d="M6 3l12 8-5.2 1.4L10.5 18z" fill="currentColor" />);
    case 'playerA':
    case 'playerB':
    case 'playerN':
    case 'playerC':
    case 'playerD':
      return <TeamSwatch team={id.slice(6) as DiagramTeam} className={cn(className, 'shrink-0')} />;
    case 'cone':
      return svg(<polygon points="12,3 20,20 4,20" fill="#f97316" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round" />);
    case 'pole':
      return svg(<circle cx="12" cy="12" r="4" fill="#fde047" stroke="#1f2937" strokeWidth="4" />);
    case 'ring':
      return svg(
        <>
          <circle cx="12" cy="12" r="7.5" fill="none" stroke="#1f2937" strokeWidth="4" opacity="0.4" />
          <circle cx="12" cy="12" r="7.5" fill="none" stroke="#fb923c" strokeWidth="2.4" />
        </>,
      );
    case 'hurdle':
      // Zwei Pfosten mit zwei Latten - deutlich anders als die Masslinie (eine Linie mit Endstrichen).
      return svg(<path d="M5 20V6M19 20V6M5 9h14M5 14h14" {...stroke} />);
    case 'ball':
      return svg(<circle cx="12" cy="12" r="7" fill="#ffffff" stroke="#111827" strokeWidth="2" />);
    case 'goal':
      return svg(<rect x="3" y="8" width="18" height="9" fill="rgba(148,163,184,0.35)" stroke="currentColor" strokeWidth="2" />);
    case 'zone':
      return svg(<rect x="3" y="5" width="18" height="14" fill="rgba(148,163,184,0.3)" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2" />);
    case 'arrowPASS':
      return svg(<path d="M3 19L19 5M19 5h-8M19 5v8" {...stroke} />);
    case 'arrowRUN':
      return svg(<path d="M3 19L19 5M19 5h-8M19 5v8" {...stroke} strokeDasharray="3 3" />);
    case 'arrowDRIBBLE':
      return svg(<path d="M3 15q3-6 6 0t6 0t6-4" {...stroke} />);
    case 'arrowLOFT':
      return svg(
        <>
          <path d="M3 19Q8 3 17 9" {...stroke} strokeDasharray="4 2.2 0.1 2.2" />
          <path d="M13 6l4 3-4 4" {...stroke} strokeWidth={1.8} />
        </>,
      );
    case 'text':
      return svg(<path d="M5 6h14M12 6v13M9 19h6" {...stroke} />);
    case 'arrowSWITCH':
      return svg(
        <>
          <path d="M3 19Q8 8 19 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="0.1 3.6" />
          <path d="M14 3l5 3-3 5" {...stroke} strokeWidth={1.8} />
        </>,
      );
    case 'measure':
      return svg(<path d="M3 12H21M3 7V17M21 7V17" {...stroke} />);
    case 'arrowSHOT':
      return svg(
        <>
          <path d="M3 19L19 5" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
          <path d="M3 19L17 7" fill="none" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" />
        </>,
      );
    case 'undo':
      return svg(<path d="M9 7L4 12l5 5M4 12h10a6 6 0 010 8" {...stroke} />);
    case 'redo':
      return svg(<path d="M15 7l5 5-5 5M20 12H10a6 6 0 000 8" {...stroke} />);
    case 'grid':
      return svg(<path d="M4 4h16v16H4zM4 9.3h16M4 14.7h16M9.3 4v16M14.7 4v16" {...stroke} strokeWidth={1.6} />);
    case 'steps':
      // Eine Schrittnummer wie am Pfeil: Kreis mit einer 1.
      return svg(
        <>
          <circle cx="12" cy="12" r="8.5" {...stroke} strokeWidth={1.8} />
          <path d="M10.2 9.8l2.1-1.4v7.2" {...stroke} strokeWidth={1.8} />
        </>,
      );
    case 'setup':
      return svg(
        <>
          <rect x="4" y="4" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="3 2" />
          <path d="M12 8v8M8 12h8" {...stroke} />
        </>,
      );
    case 'rotate':
      return svg(<path d="M19 12a7 7 0 11-2.5-5.4M19 4v4h-4" {...stroke} />);
    case 'trash':
      return svg(<path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" {...stroke} />);
    case 'clear':
    case 'close':
      return svg(<path d="M5 5l14 14M19 5L5 19" {...stroke} />);
    case 'expand':
      return svg(<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" {...stroke} />);
    case 'view':
      return svg(
        <>
          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" {...stroke} />
          <circle cx="12" cy="12" r="2.8" {...stroke} />
        </>,
      );
    case 'multi':
      return svg(
        <>
          <rect x="3" y="3" width="13" height="13" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2" />
          <path d="M9 14l3 3 7-8" {...stroke} />
        </>,
      );
    case 'all':
      return svg(
        <>
          <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2" />
          <g fill="currentColor">
            <circle cx="8.5" cy="8.5" r="1.6" />
            <circle cx="15.5" cy="8.5" r="1.6" />
            <circle cx="8.5" cy="15.5" r="1.6" />
            <circle cx="15.5" cy="15.5" r="1.6" />
          </g>
        </>,
      );
    case 'formation':
      return svg(
        <g fill="currentColor">
          {([[12, 20], [5, 14], [12, 14], [19, 14], [8, 8], [16, 8], [12, 3.5]] as const).map(([cx, cy]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2} />)}
        </g>,
      );
    case 'snap':
      return svg(
        <>
          <path d="M12 3v18M3 12h18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.5 2" strokeLinecap="round" />
          <circle cx="12" cy="12" r="3.4" fill="currentColor" />
        </>,
      );
    case 'help':
      return svg(
        <>
          <circle cx="12" cy="12" r="9" {...stroke} />
          <path d="M9.5 9.5a2.6 2.6 0 115 .9c0 1.6-2.5 2-2.5 3.6" {...stroke} />
          <circle cx="12" cy="17.2" r="0.6" fill="currentColor" stroke="currentColor" />
        </>,
      );
    case 'align':
      return svg(<path d="M4 3v18M8 6h12M8 12h8M8 18h10" {...stroke} />);
    case 'duplicate':
      return svg(
        <>
          <rect x="8" y="8" width="12" height="12" rx="2" {...stroke} />
          <path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2" {...stroke} />
        </>,
      );
    case 'row':
      return svg(
        <g fill="currentColor">
          <circle cx="5" cy="12" r="2.2" />
          <circle cx="12" cy="12" r="2.2" />
          <circle cx="19" cy="12" r="2.2" />
        </g>,
      );
    case 'block':
      return svg(<path d="M4 15v4h16v-4M12 4v10M8 10l4 4 4-4" {...stroke} />);
    case 'play':
      return svg(<path d="M8 5l11 7-11 7z" fill="currentColor" />);
  }
}

/**
 * Knopf der Leiste. Werkzeuge sind Umschalter (`pressed`), Aktionen nicht. `layout="stack"` stellt das Symbol ueber den Text
 * (Werkzeuge), `inline` daneben (Aktionen). Trefferflaeche mindestens 44 Pixel hoch.
 */
export function ToolButton({
  label,
  short,
  icon,
  pressed,
  expanded,
  controls,
  disabled,
  layout = 'stack',
  fill = false,
  onClick,
}: {
  label: string;
  short: string;
  icon: IconId;
  /** Nur bei Werkzeugen und Schaltern gesetzt; Aktionsknoepfe sind keine Umschalter. */
  pressed?: boolean;
  /** Nur beim Knopf eines auf- und zuklappbaren Bereichs: ist er offen? */
  expanded?: boolean;
  controls?: string;
  disabled?: boolean;
  layout?: 'stack' | 'inline';
  /** Fuellt die Zelle eines Rasters (am Handy). */
  fill?: boolean;
  onClick: () => void;
}): JSX.Element {
  const active = pressed || expanded;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      aria-expanded={expanded}
      aria-controls={controls}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-xl border text-[0.7rem] font-medium leading-tight shadow-sm transition-colors',
        layout === 'stack' ? 'flex min-h-11 flex-col items-center justify-center gap-0.5 px-1 py-1' : 'inline-flex min-h-11 items-center justify-center gap-1.5 px-2 py-1 text-xs sm:px-2.5 sm:text-sm',
        layout === 'stack' && (fill ? 'w-full min-w-0' : 'min-w-[3.5rem]'),
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-45',
        active ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
      )}
    >
      <ToolIcon id={icon} className={layout === 'inline' ? 'h-4 w-4' : 'h-5 w-5'} />
      <span>{short}</span>
    </button>
  );
}

export { ToolPanel, type ToolPanelProps, type ToolbarOrientation } from './DiagramToolBar';
